import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight, Play, Pause, ArrowRight, Star, MapPin, Calendar } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface Slide {
  id: number
  image: string
  title: string
  subtitle: string
  description: string
  ctaText: string
  ctaLink: string
  animationStyle: 'slide' | 'fade' | 'zoom' | 'flip'
  contentAnimation: 'slideUp' | 'slideLeft' | 'slideRight' | 'bounce'
  accentColor: string
}

const slides: Slide[] = [
  {
    id: 1,
    image: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    title: 'Discover Your Next',
    subtitle: 'Adventure',
    description: 'Experience the world\'s most breathtaking destinations with our expert travel planning.',
    ctaText: 'Explore Destinations',
    ctaLink: '/destinations',
    animationStyle: 'slide',
    contentAnimation: 'slideUp',
    accentColor: 'from-blue-500 to-purple-600'
  },
  {
    id: 2,
    image: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    title: 'Luxury Travel',
    subtitle: 'Experiences',
    description: 'Indulge in premium accommodations and exclusive experiences tailored to your preferences.',
    ctaText: 'View Luxury Tours',
    ctaLink: '/destinations',
    animationStyle: 'zoom',
    contentAnimation: 'slideLeft',
    accentColor: 'from-emerald-500 to-teal-600'
  },
  {
    id: 3,
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    title: 'Adventure Awaits',
    subtitle: 'Everywhere',
    description: 'From mountain peaks to ocean depths, create unforgettable memories with every journey.',
    ctaText: 'Start Adventure',
    ctaLink: '/destinations',
    animationStyle: 'fade',
    contentAnimation: 'bounce',
    accentColor: 'from-orange-500 to-red-600'
  },
  {
    id: 4,
    image: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    title: 'Cultural',
    subtitle: 'Immersion',
    description: 'Connect with local cultures and traditions for authentic travel experiences.',
    ctaText: 'Cultural Tours',
    ctaLink: '/destinations',
    animationStyle: 'flip',
    contentAnimation: 'slideRight',
    accentColor: 'from-purple-500 to-pink-600'
  }
]

interface ImageSliderProps {
  autoPlay?: boolean
  interval?: number
  showControls?: boolean
  showIndicators?: boolean
}

const ImageSlider = ({ 
  autoPlay = true, 
  interval = 5000, 
  showControls = true, 
  showIndicators = true 
}: ImageSliderProps) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isPlaying, setIsPlaying] = useState(autoPlay)
  const navigate = useNavigate()

  useEffect(() => {
    if (!isPlaying) return

    const timer = setInterval(() => {
      setCurrentSlide((prev: number) => (prev + 1) % slides.length)
    }, interval)

    return () => clearInterval(timer)
  }, [isPlaying, interval])

  const nextSlide = () => {
    setCurrentSlide((prev: number) => (prev + 1) % slides.length)
  }

  const prevSlide = () => {
    setCurrentSlide((prev: number) => (prev - 1 + slides.length) % slides.length)
  }

  const goToSlide = (index: number) => {
    setCurrentSlide(index)
  }

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying)
  }

  // Enhanced animation variants
  const slideVariants = {
    slide: {
      initial: { x: '100%', opacity: 0 },
      animate: { x: 0, opacity: 1 },
      exit: { x: '-100%', opacity: 0 }
    },
    fade: {
      initial: { opacity: 0, scale: 0.8 },
      animate: { opacity: 1, scale: 1 },
      exit: { opacity: 0, scale: 1.2 }
    },
    zoom: {
      initial: { scale: 1.5, opacity: 0 },
      animate: { scale: 1, opacity: 1 },
      exit: { scale: 0.5, opacity: 0 }
    },
    flip: {
      initial: { rotateY: 90, opacity: 0 },
      animate: { rotateY: 0, opacity: 1 },
      exit: { rotateY: -90, opacity: 0 }
    }
  }

  const contentVariants = {
    slideUp: {
      initial: { y: 50, opacity: 0 },
      animate: { y: 0, opacity: 1 },
      transition: { duration: 0.8, ease: "easeOut" }
    },
    slideLeft: {
      initial: { x: -100, opacity: 0 },
      animate: { x: 0, opacity: 1 },
      transition: { duration: 0.8, ease: "easeOut" }
    },
    slideRight: {
      initial: { x: 100, opacity: 0 },
      animate: { x: 0, opacity: 1 },
      transition: { duration: 0.8, ease: "easeOut" }
    },
    bounce: {
      initial: { y: 30, opacity: 0 },
      animate: { y: 0, opacity: 1 },
      transition: { 
        duration: 0.8, 
        ease: [0.68, -0.55, 0.265, 1.55]
      }
    }
  }

  const currentSlideData = slides[currentSlide]
  const slideVariant = slideVariants[currentSlideData.animationStyle]
  const contentVariant = contentVariants[currentSlideData.contentAnimation]

  return (
    <div className="relative h-screen overflow-hidden bg-black">
      {/* Slides */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={slideVariant.initial}
          animate={slideVariant.animate}
          exit={slideVariant.exit}
          transition={{ duration: 1.2, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          {/* Background Image with Enhanced Overlay */}
          <div className="absolute inset-0">
            <img
              src={currentSlideData.image}
              alt={currentSlideData.title}
              className="w-full h-full object-cover"
            />
            {/* Enhanced gradient overlays */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/70"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-br from-black/30 via-transparent to-black/60"></div>
          </div>

          {/* Content */}
          <div className="relative z-10 flex items-center justify-center h-full">
            <div className="text-center text-white max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="mb-6"
              >
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-gradient-to-r ${currentSlideData.accentColor} text-white shadow-lg`}>
                  <Star className="h-4 w-4 mr-2" />
                  Premium Travel Experience
                </span>
              </motion.div>

              {/* Title */}
              <motion.div
                initial={contentVariant.initial}
                animate={contentVariant.animate}
                transition={contentVariant.transition}
                className="mb-6"
              >
                <h1 className="text-6xl md:text-8xl font-bold mb-4 leading-tight tracking-tight">
                  {currentSlideData.title}
                  <span className={`block bg-gradient-to-r ${currentSlideData.accentColor} bg-clip-text text-transparent`}>
                    {currentSlideData.subtitle}
                  </span>
                </h1>
              </motion.div>

              {/* Description */}
              <motion.div
                initial={contentVariant.initial}
                animate={contentVariant.animate}
                transition={{ ...contentVariant.transition, delay: 0.2 }}
                className="mb-8"
              >
                <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed font-light">
                  {currentSlideData.description}
                </p>
              </motion.div>

              {/* CTA Button */}
              <motion.div
                initial={contentVariant.initial}
                animate={contentVariant.animate}
                transition={{ ...contentVariant.transition, delay: 0.4 }}
                className="mb-8"
              >
                <button 
                  onClick={() => navigate(currentSlideData.ctaLink)}
                  className={`group bg-gradient-to-r ${currentSlideData.accentColor} hover:shadow-2xl text-white font-bold py-5 px-10 rounded-2xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 shadow-xl cursor-pointer`}
                >
                  <span className="flex items-center space-x-3">
                    <span>{currentSlideData.ctaText}</span>
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </button>
              </motion.div>

              {/* Features */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-wrap justify-center gap-8 text-sm text-gray-300"
              >
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4 text-blue-400" />
                  <span>50+ Destinations</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-green-400" />
                  <span>24/7 Support</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Star className="h-4 w-4 text-yellow-400" />
                  <span>5-Star Service</span>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Enhanced Navigation Controls */}
      {showControls && (
        <>
          {/* Previous Button */}
          <motion.button
            whileHover={{ scale: 1.1, rotate: -5, x: -5 }}
            whileTap={{ scale: 0.9 }}
            onClick={prevSlide}
            className="absolute left-6 top-1/2 transform -translate-y-1/2 z-20 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white p-4 rounded-full transition-all duration-300 border border-white/20"
          >
            <ChevronLeft className="h-7 w-7" />
          </motion.button>

          {/* Next Button */}
          <motion.button
            whileHover={{ scale: 1.1, rotate: 5, x: 5 }}
            whileTap={{ scale: 0.9 }}
            onClick={nextSlide}
            className="absolute right-6 top-1/2 transform -translate-y-1/2 z-20 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white p-4 rounded-full transition-all duration-300 border border-white/20"
          >
            <ChevronRight className="h-7 w-7" />
          </motion.button>

          {/* Play/Pause Button */}
          <motion.button
            whileHover={{ scale: 1.1, rotate: 180 }}
            whileTap={{ scale: 0.9 }}
            onClick={togglePlayPause}
            className="absolute top-6 right-6 z-20 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white p-3 rounded-full transition-all duration-300 border border-white/20"
          >
            {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
          </motion.button>
        </>
      )}

      {/* Enhanced Indicators */}
      {showIndicators && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex space-x-4">
          {slides.map((_, index) => (
            <motion.button
              key={index}
              whileHover={{ scale: 1.3, y: -3 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => goToSlide(index)}
              className={`w-4 h-4 rounded-full transition-all duration-300 border-2 ${
                index === currentSlide
                  ? 'bg-white scale-125 border-white'
                  : 'bg-white/30 hover:bg-white/60 border-white/50'
              }`}
            />
          ))}
        </div>
      )}

      {/* Enhanced Slide Counter */}
      <motion.div 
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className="absolute top-6 left-6 z-20 bg-white/10 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm border border-white/20"
      >
        {currentSlide + 1} / {slides.length}
      </motion.div>

      {/* Enhanced Floating Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Floating Elements with enhanced animations */}
        <motion.div
          animate={{ 
            y: [0, -30, 0],
            rotate: [0, 10, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{ 
            duration: 8, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
          className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm rounded-full border border-white/20"
        />
        
        <motion.div
          animate={{ 
            y: [0, 25, 0],
            rotate: [0, -15, 0],
            x: [0, 15, 0]
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity, 
            ease: "easeInOut",
            delay: 2
          }}
          className="absolute top-40 right-20 w-16 h-16 bg-gradient-to-r from-green-500/20 to-teal-500/20 backdrop-blur-sm rounded-full border border-white/20"
        />

        <motion.div
          animate={{ 
            y: [0, -20, 0],
            x: [0, 12, 0],
            rotate: [0, 15, 0]
          }}
          transition={{ 
            duration: 9, 
            repeat: Infinity, 
            ease: "easeInOut",
            delay: 1
          }}
          className="absolute bottom-40 left-20 w-24 h-24 bg-gradient-to-r from-orange-500/20 to-red-500/20 backdrop-blur-sm rounded-full border border-white/20"
        />

        <motion.div
          animate={{ 
            y: [0, 35, 0],
            rotate: [0, -20, 0],
            scale: [1, 0.8, 1]
          }}
          transition={{ 
            duration: 12, 
            repeat: Infinity, 
            ease: "easeInOut",
            delay: 3
          }}
          className="absolute bottom-20 right-10 w-18 h-18 bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-sm rounded-full border border-white/20"
        />

        <motion.div
          animate={{ 
            y: [0, -40, 0],
            x: [0, -20, 0],
            rotate: [0, 25, 0]
          }}
          transition={{ 
            duration: 11, 
            repeat: Infinity, 
            ease: "easeInOut",
            delay: 4
          }}
          className="absolute top-60 left-1/2 w-12 h-12 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 backdrop-blur-sm rounded-full border border-white/20"
        />
      </div>
    </div>
  )
}

export default ImageSlider
